import { CSSProperties, memo, Suspense, useMemo } from 'react';
import { isNumber } from 'lodash';

import {
  DefaultPicColorRanges,
  DefaultPicNormalColor,
} from '../../../constants/colorPairs';
import { Icons, IconType } from '../../../constants/icons';
import { DefaultColors } from '../../../constants/Theme/DefaultColors';

import './DefaultProfilePic.css';

export type ColorPairs = [string, string];

export interface DefaultProfilePicProps {
  userId: string;
  privateMode?: boolean;
  name?: string;
  index?: number;
  style?: CSSProperties;
}

export const getRandomDefaultProfilePictureIndex = (
  userId: string,
  privateMode?: boolean,
) => {
  let random = userId ? parseInt(userId.substring(32, 36), 16) % 11 : 0;
  if (Number.isNaN(random) || privateMode) {
    random = Math.floor(Math.random() * 11);
  }
  if (userId === '0') {
    random = 0;
  }

  return Number.isNaN(random) === false && !privateMode ? random : undefined;
};

export const DefaultProfilePic = memo(
  ({ userId, privateMode, name, index }: DefaultProfilePicProps) => {
    const random = useMemo(
      () => getRandomDefaultProfilePictureIndex(userId, privateMode),
      [privateMode, userId],
    );

    const profilePics = [
      Icons[IconType.ProfilePic1],
      Icons[IconType.ProfilePic2],
      Icons[IconType.ProfilePic3],
      Icons[IconType.ProfilePic4],
      Icons[IconType.ProfilePic5],
      Icons[IconType.ProfilePic6],
      Icons[IconType.ProfilePic7],
      Icons[IconType.ProfilePic8],
      Icons[IconType.ProfilePic9],
      Icons[IconType.ProfilePic10],
      Icons[IconType.ProfilePic11],
    ];

    const colors = useMemo<ColorPairs>(() => {
      if (isNumber(index)) {
        const i = index % DefaultPicNormalColor.length;
        const color =
          DefaultPicNormalColor[i]?.borderColor ?? DefaultColors.red.CC_PINK;
        return [color, color];
      }
      return isNumber(random)
        ? (DefaultPicColorRanges[random] as ColorPairs)
        : ['#f9bfd0', '#ca5d86'];
    }, [index, random]);

    const Picture = isNumber(random) ? profilePics[random] : undefined;

    return Picture ? (
      <Suspense>
        <img src={Picture} className="default-profile-pic-img" />
      </Suspense>
    ) : (
      <div
        className="default-profile-container"
        style={{
          background: `linear-gradient(to right, ${colors[0]}, ${colors[1]})`,
        }}
      >
        <p className="default-profile-text">{name}</p>
      </div>
    );
  },
);
